import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Profile } from 'src/app/general-components/profile/profile';
import { BASE_INDI_URL } from 'src/app/injection-tokens';
import { AuthService } from 'src/app/auth/auth.service';
import { FreshdeskTicketLinkPipe } from 'src/app/pipes/freshdesk-ticket-link/freshdesk-ticket-link.pipe';
import { ErrorResponse } from 'src/app/interfaces/bad-request';
import { HttpErrorResponse } from '@angular/common/http';
import { CombinedData, NewSalesorderService } from '../new-salesorder/new-salesorder.service';
import { APIOrderlineToWrite, APIVerkooporderToWrite } from '../APIVerkoopordersToWrite';
import { Address } from '../indi-customer';
import { SHIPPING_METHOD_CODES } from '../new-salesorder/shipping-methods.const';
import { APIOrderline, APIVerkooporderOrderDetails } from '../APIVerkooporders';
import { RedirectService } from './redirect.service';

@Component({
  selector: 'app-blind-salesorder',
  templateUrl: './blind-salesorder.component.html',
  styleUrls: ['./blind-salesorder.component.scss'],
})
export class BlindSalesorderComponent implements OnInit, AfterViewInit, OnDestroy {
  profile!: Profile;
  ids: number[] = [];
  error: string = '';

  constructor(
    @Inject(BASE_INDI_URL) readonly baseIndiUrl: string,
    private route: ActivatedRoute,
    private service: NewSalesorderService,
    private authService: AuthService,
    private freshdeskLink: FreshdeskTicketLinkPipe,
    private redirectService: RedirectService
  ) {}

  ngOnInit() {
    document.body.classList.add('bare-page');
  }

  ngAfterViewInit(): void {
    this.profile = this.authService.getUserInfo();
    this.ids = this.route.snapshot.queryParamMap
      .getAll('id')
      .map(id => Number(id))
      .filter(id => !isNaN(id) && id !== 0);

    if (this.ids.length > 0) {
      this.service.getCombinedData(this.ids).subscribe({
        next: (combinedData: CombinedData) => {
          if (!combinedData.verkoopordersCustomerPreferences.preferredUserUUID) {
            this.error =
              'Er is geen voorkeursgebruiker ingesteld of deze is verwijderd na het aanmaken van de ticket.\nEr is geen Verkooporder aangemaakt.';
            return;
          }
          const orderDetails = combinedData.APIVerkooporders.verkooporder.orderDetails;
          if (!orderDetails) {
            this.error =
              'Er zijn geen orderdetails gevonden in deze verkooporder ticket.\nEr is geen Verkooporder aangemaakt.';
            return;
          }
          this.service
            .postAPIVerkoopordersToWrite({
              ids: this.ids,
              verkooporder: this.createVerkooporder(combinedData, orderDetails),
            })
            .subscribe({
              next: () => {
                this.redirectService.go(
                  this.freshdeskLink.transform(combinedData.APIVerkooporders.ticketIds[0].toString())
                );
              },
              error: (error: HttpErrorResponse) => {
                console.error(error);
                const httpError = error.error as ErrorResponse;
                const exception = httpError.exception ? ': \n' + httpError.exception : '';
                this.error = '\nEr is iets fout gegaan met het verzenden van de verkooporder' + exception;
              },
            });
        },
        error: (error: HttpErrorResponse) => {
          console.error(error);
          const httpError = error.error as ErrorResponse;
          const exception = httpError.exception ? ': \n' + httpError.exception : '';
          this.error = '\nEr is iets fout gegaan met het ophalen van de verkooporder' + exception;
        },
      });
    }
  }

  createVerkooporder(combinedData: CombinedData, orderDetails: APIVerkooporderOrderDetails): APIVerkooporderToWrite {
    const preferredUserUUID = combinedData.verkoopordersCustomerPreferences.preferredUserUUID ?? '';
    const preferredUser = combinedData.users.find(u => u.uuid === preferredUserUUID);
    const shippingMethod = this.typeOfShipping(
      combinedData.indiCustomer.hasNightshipping,
      combinedData.indiCustomer.hasPickUpAtCounter ?? false
    );
    const shippingAddresses = combinedData.indiCustomer.addresses.filter(
      address => address.type !== 'atDefaultForInvoice' && address.type !== 'atForInvoice'
    );
    const deliveryAddress: Address = this.getDeliveryAddress(
      shippingMethod,
      combinedData.indiCustomer.addresses.filter(address => address.nightShipping)[0],
      shippingAddresses[0]
    );

    return {
      orderDetails: {
        customer: {
          invoiceEmailAddress: preferredUser?.invoicesEmailAddress ?? '',
          userUUID: preferredUserUUID,
        },
        customerRef: orderDetails.customerRef ?? '',
        shippingMethod: SHIPPING_METHOD_CODES[shippingMethod],
        fixedDeliveryDate: orderDetails.fixedDeliveryDate,
        deliveryAddress: {
          ...deliveryAddress,
          email: preferredUser?.loginEmailAddress ?? '',
        },
        orderlines: this.getOrderlines(orderDetails.orderlines),
      },
      reason: 'Blind doorgezet',
      createdByHint: this.profile.fullName,
    };
  }

  typeOfShipping(hasNightShipping: boolean, hasPickUpAtCounter: boolean): string {
    if (hasNightShipping) {
      return 'Nachtbezorging';
    } else if (hasPickUpAtCounter) {
      return 'Afhalen bij de balie';
    } else {
      return 'Dagbezorging';
    }
  }

  getDeliveryAddress(shippingMethod: string, nightshippingAddress: Address, defaultShippingAddress: Address): Address {
    switch (shippingMethod) {
      case 'Nachtbezorging':
        return nightshippingAddress;
      case 'Dagbezorging':
      case 'Afhalen bij de balie':
      default:
        return defaultShippingAddress;
    }
  }

  getOrderlines(orderlines: APIOrderline[]): APIOrderlineToWrite[] {
    return orderlines.map(l => ({
      rowNumber: l.rowNumber,
      item: { sku: l.item.sku },
      quantity: l.quantity,
      orderlineRef: l.orderlineRef,
    }));
  }

  returnToFreshdesk() {
    this.redirectService.back();
  }

  ngOnDestroy() {
    document.body.classList.remove('bare-page');
  }
}
